import type { ReactNode } from 'react'
import { cn, formatShortDate } from '@/lib/format'
import { accountStatusLabel } from '@/lib/status'
import type { Account, AccountStatus, TenantSource } from '@/api/types'

export type BadgeTone = 'good' | 'warn' | 'bad' | 'neutral' | 'accent' | 'outline'

const TONES: Record<BadgeTone, string> = {
  good: 'bg-good-soft text-good',
  warn: 'bg-warn-soft text-warn',
  bad: 'bg-bad-soft text-bad',
  neutral: 'bg-neutral-soft text-neutral',
  accent: 'bg-accent-soft text-accent-ink',
  outline: 'border border-line-strong text-muted',
}

const DOTS: Record<BadgeTone, string> = {
  good: 'bg-good',
  warn: 'bg-warn',
  bad: 'bg-bad',
  neutral: 'bg-neutral',
  accent: 'bg-accent',
  outline: 'bg-muted',
}

export interface BadgeProps {
  tone?: BadgeTone
  /** Leading status dot. */
  dot?: boolean
  title?: string
  className?: string
  children: ReactNode
}

/** Small uppercase label. The building block for every status pill. */
export function Badge({ tone = 'neutral', dot, title, className, children }: BadgeProps) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1.5 rounded px-2 py-[5px] text-2xs font-bold uppercase leading-none tracking-label whitespace-nowrap',
        TONES[tone],
        className,
      )}
    >
      {dot ? <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', DOTS[tone])} /> : null}
      {children}
    </span>
  )
}

// ─── Account status ─────────────────────────────────────────────────────────

const STATUS_TONE: Record<AccountStatus, BadgeTone> = {
  trialing: 'accent',
  active: 'good',
  past_due: 'warn',
  suspended: 'bad',
  canceled: 'neutral',
}

/**
 * One account status. A trial whose end date has passed reads "Trial ended"
 * (the public site is down), and a legacy "no subscription" row reads
 * "No plan" instead of a bare "Canceled".
 */
export function AccountStatusPill({ account }: { account: Pick<Account, 'status' | 'is_live' | 'legacy_status'> }) {
  if (account.status === 'trialing' && !account.is_live) {
    return <Badge tone="warn">Trial ended</Badge>
  }
  if (account.status === 'canceled' && account.legacy_status === 'none') {
    return (
      <Badge tone="outline" title="No subscription on record">
        No plan
      </Badge>
    )
  }
  if (account.status === 'canceled' && account.legacy_status === 'expired') {
    return <Badge tone="neutral">Expired</Badge>
  }
  return <Badge tone={STATUS_TONE[account.status]}>{accountStatusLabel(account.status)}</Badge>
}

/** Whether the public site is serving. */
export function LivePill({ isLive }: { isLive: boolean }) {
  return isLive ? (
    <Badge tone="good" dot>
      Live
    </Badge>
  ) : (
    <Badge tone="bad" dot>
      Offline
    </Badge>
  )
}

/** The package. `standard`, a legacy package name, or none. */
export function PlanPill({ account }: { account: Pick<Account, 'package_key' | 'package_name'> }) {
  if (!account.package_key) return <Badge tone="outline">No package</Badge>
  const label = account.package_key === 'standard' ? 'Standard' : account.package_name || account.package_key.replace(/_/g, ' ')
  return <Badge tone="neutral">{label}</Badge>
}

/** #1387: "Self-serve" for a league from the public signup; nothing for a portal-made one. */
export function SourcePill({ source }: { source: TenantSource | undefined }) {
  if (source !== 'self_serve') return null
  return (
    <Badge tone="outline" title="Signed up on scorelm.com (self-serve)">
      Self-serve
    </Badge>
  )
}

/** "Trial ends 12 Oct" / "Renews 1 Nov", or nothing. */
export function RenewalPill({ account }: { account: Pick<Account, 'status' | 'trial_ends_at' | 'current_period_end'> }) {
  if (account.status === 'trialing' && account.trial_ends_at) {
    return <Badge tone="accent">Trial ends {formatShortDate(account.trial_ends_at)}</Badge>
  }
  if ((account.status === 'active' || account.status === 'past_due') && account.current_period_end) {
    return <Badge tone="accent">Renews {formatShortDate(account.current_period_end)}</Badge>
  }
  return null
}
